import { useState, memo } from "react";

/**
 * ExpenseChart — SVG doughnut chart showing expenses by category.
 *
 * Props:
 *   data — array of { category: string, amount: number } sorted desc
 */

const PALETTE = [
  "#0C2D48", "#82B3CC", "#E4E69B", "#FBC490", "#6abf85",
  "#5352ed", "#A12710", "#D7C8F6", "#35454C", "#a29bfe",
  "#fd79a8", "#7a4a10",
];

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, outerR, innerR, startDeg, endDeg) {
  const p1 = polarToCartesian(cx, cy, outerR, startDeg);
  const p2 = polarToCartesian(cx, cy, outerR, endDeg);
  const p3 = polarToCartesian(cx, cy, innerR, endDeg);
  const p4 = polarToCartesian(cx, cy, innerR, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
    `L ${p3.x.toFixed(2)} ${p3.y.toFixed(2)}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${p4.x.toFixed(2)} ${p4.y.toFixed(2)}`,
    "Z",
  ].join(" ");
}

function fmtMoney(n) {
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ExpenseChart({ data }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: "center", color: "#bbb", padding: "2rem", fontSize: "0.88rem" }}>
        No expenses for this period.
      </div>
    );
  }

  const total = data.reduce((s, d) => s + d.amount, 0);
  if (total === 0) {
    return (
      <div style={{ textAlign: "center", color: "#bbb", padding: "2rem", fontSize: "0.88rem" }}>
        No expenses for this period.
      </div>
    );
  }

  // ── Build arc segments ────────────────────────────────────────────────────
  const CX = 100, CY = 100, OUTER = 90, INNER = 52;
  let cursor = 0;
  const segments = data.map((d, i) => {
    const startDeg = cursor;
    const sweep    = (d.amount / total) * 360;
    const endDeg   = cursor + sweep;
    cursor = endDeg;
    const isHovered = hoveredIdx === i;
    return {
      ...d,
      color: PALETTE[i % PALETTE.length],
      path: arcPath(CX, CY, isHovered ? OUTER + 6 : OUTER, INNER, startDeg, endDeg),
      pct: ((d.amount / total) * 100).toFixed(1),
    };
  });

  return (
    <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "flex-start" }}>
      {/* Doughnut */}
      <svg
        viewBox="0 0 200 200"
        style={{ width: 200, height: 200, flexShrink: 0 }}
        aria-label="Expense breakdown doughnut chart"
      >
        {segments.map((seg, i) => (
          <path
            key={i}
            d={seg.path}
            fill={seg.color}
            opacity={hoveredIdx == null || hoveredIdx === i ? 1 : 0.6}
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{ cursor: "pointer", transition: "opacity 0.15s" }}
          />
        ))}
        {/* Centre label */}
        <text x={CX} y={CY - 6}  textAnchor="middle" fontSize={11} fontWeight={700} fill="#0C2D48" fontFamily="Raleway, sans-serif">
          {hoveredIdx != null ? `${segments[hoveredIdx].pct}%` : "Total"}
        </text>
        <text x={CX} y={CY + 10} textAnchor="middle" fontSize={9}  fill="#888" fontFamily="Raleway, sans-serif">
          {hoveredIdx != null
            ? fmtMoney(segments[hoveredIdx].amount)
            : fmtMoney(total)}
        </text>
      </svg>

      {/* Legend */}
      <div style={{ flex: 1, minWidth: 200 }}>
        {segments.map((seg, i) => (
          <div
            key={i}
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{
              display: "flex", alignItems: "center", gap: "0.6rem",
              padding: "0.3rem 0.4rem", borderRadius: 6,
              background: hoveredIdx === i ? "#f8f9fb" : "transparent",
              cursor: "pointer", transition: "background 0.1s",
              marginBottom: "0.15rem",
            }}
          >
            <div style={{ width: 12, height: 12, borderRadius: 3, background: seg.color, flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: "0.82rem", color: "#333", fontFamily: "Raleway, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {seg.category}
            </span>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0C2D48", whiteSpace: "nowrap", fontFamily: "Raleway, sans-serif" }}>
              {fmtMoney(seg.amount)}
            </span>
            <span style={{ fontSize: "0.75rem", color: "#aaa", whiteSpace: "nowrap", minWidth: 40, textAlign: "right", fontFamily: "Raleway, sans-serif" }}>
              {seg.pct}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default memo(ExpenseChart);
